/**
 * Format a number with the "fr-FR" locale used everywhere on this screen
 * (thousands separators are non-breaking spaces, decimal mark is comma).
 * Returns "0" when the input is null/undefined to keep KPI cards stable.
 */
export const formatInteger = (value: number | undefined | null): string => {
    if (value === undefined || value === null || Number.isNaN(value)) return "0";
    return Math.round(value).toLocaleString("fr-FR");
};

/**
 * Like `formatInteger`, but renders "—" for null/undefined/NaN instead of "0"
 * so a missing value is visually distinct from a real zero.
 */
export const formatIntegerOrDash = (value: number | undefined | null): string => {
    if (value === undefined || value === null || Number.isNaN(value)) return "—";
    return Math.round(value).toLocaleString("fr-FR");
};

/** One-decimal float, same locale rules as `formatInteger`. */
export const formatDecimal = (value: number | undefined | null, digits = 1): string => {
    if (value === undefined || value === null || Number.isNaN(value)) return "0";
    return value.toLocaleString("fr-FR", {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
    });
};

/** Percent value coming in as 0..100 (NOT 0..1). */
export const formatPercent = (value: number | undefined | null, digits = 1): string => {
    if (value === undefined || value === null || Number.isNaN(value)) return "—";
    return `${formatDecimal(value, digits)}%`;
};

/**
 * Render a count of seconds as "M min SS" (e.g. 872 -> "14 min 32").
 * Only used by the engagement KPI today; safe to call with null (returns "—").
 */
export const formatDuration = (seconds: number | undefined | null): string => {
    if (seconds === undefined || seconds === null || Number.isNaN(seconds)) return "—";
    const total = Math.max(0, Math.round(seconds));
    const minutes = Math.floor(total / 60);
    const remaining = total % 60;
    return `${minutes} min ${remaining.toString().padStart(2, "0")}`;
};

/** Rounded minutes only (e.g. 4694 s → "78 min"). */
export const formatDurationMinutes = (seconds: number | undefined | null): string => {
    if (seconds === undefined || seconds === null || Number.isNaN(seconds)) return "—";
    const minutes = Math.round(Math.max(0, seconds) / 60);
    return `${minutes} min`;
};
